import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { format, parse, isValid } from 'date-fns';

import { Box } from '@mui/system';
import { Typography, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { MultiCombobox } from '@bestdoctor/spectrum-uikit/MultiCombobox';
import { Combobox } from '@bestdoctor/spectrum-uikit/Combobox';
import { TextField as SpectrumTextField } from '@bestdoctor/spectrum-uikit/TextField';
import { DatePicker } from '@bestdoctor/spectrum-uikit/DatePicker';
import type { ComboboxOption } from '@bestdoctor/spectrum-uikit/Combobox';
import { listPatientFilters, listpatientTitleStyle } from 'ui/patients/patients';

import { SearchParams } from 'interfaces/global/globalInterfaces';
import { IListStaffProps } from 'interfaces/patients/patientInterfaces';

import { isDateActual } from 'utils/dateHelpers';
import { patientTypes, inputTypeData, DEFAULT_STAFF_SEARCH_TYPE } from 'utils/constants';
import { TooltipCustomTableFields } from 'shared/tooltip/TooltipCustomTableFields/TooltipCustomTableFields';
import { Loader } from '@bestdoctor/spectrum-uikit/Loader';
import { TableStaff } from './TableStaff/TableStaff';

const PATIENT_SEARCH_DEBOUNCE_MS = 1000;

export const ListStaff: React.FC<IListStaffProps> = ({
  selectedCompanies,
  staffQuantity,

  isAddingForbiddenByDates,
  isDetachingForbiddenByDates,

  orderingTableStaff,

  isPatients,
  currentPatient,
  setPatient,

  searchStaffCustomField,

  startDate,
  endDate,
  handleStartDate,
  handleEndDate,

  selectedTypeStaff,
  handleSelectedTypeStaff,

  selectedPatientType,
  handleSelectedPatientType,

  selectedStaffFilterPrograms,
  setSelectedStaffFilterPrograms,

  tableUserFields,
  handleSearchStaffCustomField,

  handleSearchType,
  searchType,

  handleTableFields,

  programFilterOptions,

  isLoadingServicePeriods,
  isLoadingSearchPatients,
  isFetchingSearchPatients,

  isFetchingNextPageSearchPatients,

  handleStaffCheck,
  setSelectedStaff,
  handleAvailabilityCompletedPatients,

  handleOrderingTableStaff,
  handleActionWithStaffTooltip,
  accessList,
  rolesData,
  defaultPermissions,
  isSlotsAviable,
  refetchSearchPatients,
}) => {
  const typeFilterOptions: SearchParams[] = [
    { id: 'active', value: 'active', title: 'Активные', color: '#00bcbc' },
    { id: 'planned', value: 'planned', title: 'Планируемые', color: '#4079ad' },
    { id: 'ended', value: 'ended', title: 'Завершенные', color: '#81838a' },
  ];

  // Преобразуем Date в строку формата dd.MM.yyyy для DatePicker
  const startDateString = startDate ? format(startDate, 'dd.MM.yyyy') : undefined;
  const endDateString = endDate ? format(endDate, 'dd.MM.yyyy') : undefined;

  // Важно: Spectrum DatePicker возвращает строку и "промежуточные" значения при ручном вводе.
  // Если value вычислять только из Date (startDate/endDate), ввод будет "откатываться" до тех пор,
  // пока строка не распарсится в валидную дату. Поэтому держим отдельный input state.
  const [startDateInput, setStartDateInput] = useState<string | undefined>(startDateString);
  const [endDateInput, setEndDateInput] = useState<string | undefined>(endDateString);

  const [searchText, setSearchText] = useState<string>('');

  const [isTableTooltipOpen, setTableTooltipOpen] = useState<boolean>(false);

  /** Пока запрос ещё не попал в react-query (очистка поля без debounce и т.п.) */
  const [awaitingPatientSearchRequest, setAwaitingPatientSearchRequest] = useState(false);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isFirstSearchTypeEffect = useRef(true);

  const flushSearchToParent = useCallback(
    (value: string): void => {
      setAwaitingPatientSearchRequest(true);
      handleSearchStaffCustomField(value);
      sessionStorage.setItem('tableFilterPatientCustomSearchValue', JSON.stringify(value));
    },
    [handleSearchStaffCustomField]
  );

  const scheduleSearch = useCallback(
    (value: string): void => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = undefined;
      }
      if (value === '') {
        flushSearchToParent('');
        return;
      }
      searchDebounceRef.current = setTimeout(() => {
        searchDebounceRef.current = undefined;
        flushSearchToParent(value);
      }, PATIENT_SEARCH_DEBOUNCE_MS);
    },
    [flushSearchToParent]
  );

  useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    []
  );

  useEffect(() => {
    if (isFirstSearchTypeEffect.current) {
      isFirstSearchTypeEffect.current = false;
      return;
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = undefined;
    }
    setSearchText('');
    flushSearchToParent('');
  }, [searchType, flushSearchToParent]);

  useEffect(() => {
    setSearchText(searchStaffCustomField);
  }, [searchStaffCustomField]);

  useEffect(() => {
    if (!awaitingPatientSearchRequest) return;
    if (isFetchingSearchPatients || isLoadingSearchPatients) return;
    setAwaitingPatientSearchRequest(false);
  }, [awaitingPatientSearchRequest, isFetchingSearchPatients, isLoadingSearchPatients]);

  useEffect(() => {
    setStartDateInput(startDateString);
  }, [startDateString]);

  useEffect(() => {
    setEndDateInput(endDateString);
  }, [endDateString]);

  // Преобразуем программы в формат ComboboxOption
  const programOptions: ComboboxOption<SearchParams>[] = useMemo(
    () =>
      (programFilterOptions || []).map((program) => ({
        label: program.title,
        value: program,
      })),
    [programFilterOptions]
  );

  // Преобразуем категории в формат ComboboxOption
  const categoryOptions: ComboboxOption<SearchParams>[] = useMemo(
    () =>
      (patientTypes || []).map((category) => ({
        label: category.title,
        value: {
          ...category,
          id: String(category.id),
        } as SearchParams,
      })),
    [patientTypes]
  );

  // Преобразуем типы пациентов в формат ComboboxOption
  const patientTypeOptions: ComboboxOption<SearchParams>[] = useMemo(
    () =>
      typeFilterOptions.map((type) => ({
        label: type.title,
        value: type,
      })),
    []
  );

  // Находим текущие выбранные опции программ (сравниваем по id, при отсутствии — по title и value)
  const selectedProgramOptions = useMemo(
    () =>
      programOptions.filter((option) =>
        selectedStaffFilterPrograms.some(
          (program) =>
            (program.id != null && program.id === option.value.id) ||
            (program.title === option.value.title && program.value === option.value.value)
        )
      ),
    [programOptions, selectedStaffFilterPrograms]
  );

  // Находим текущие выбранные опции категорий
  const selectedCategoryOptions = useMemo(
    () => categoryOptions.filter((option) => selectedPatientType.some((category) => category.id === option.value.id)),
    [categoryOptions, selectedPatientType]
  );

  // Находим текущие выбранные опции типов пациентов
  const selectedPatientTypeOptions = useMemo(
    () => patientTypeOptions.filter((option) => selectedTypeStaff.some((type) => type.id === option.value.id)),
    [patientTypeOptions, selectedTypeStaff]
  );

  // Преобразуем типы поиска в формат ComboboxOption
  const searchTypeOptions: ComboboxOption<{ title: string; value: string }>[] = useMemo(
    () =>
      inputTypeData.map((type) => ({
        label: type.title,
        value: type,
      })),
    []
  );

  const selectedSearchTypeOption = useMemo(() => {
    const effective = searchType && searchType !== '' ? searchType : DEFAULT_STAFF_SEARCH_TYPE;
    return searchTypeOptions.find((option) => option.value.value === effective) ?? searchTypeOptions[0];
  }, [searchTypeOptions, searchType]);

  const isMainSearchPending =
    (isLoadingSearchPatients || isFetchingSearchPatients || awaitingPatientSearchRequest) &&
    !isFetchingNextPageSearchPatients;
  const hasPatientRows = !!isPatients?.length;

  const showStaffTotalCount = !isLoadingServicePeriods && !isMainSearchPending && staffQuantity !== 0;

  return (
    <Box component="section" sx={{ marginTop: '32px', marginBottom: '122px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={listpatientTitleStyle(false)}>
          Список пациентов
          {showStaffTotalCount && (
            <Box component="span" sx={listpatientTitleStyle(true)}>{` (всего: ${staffQuantity})`}</Box>
          )}
        </Typography>
        <TooltipCustomTableFields
          handleTableFields={handleTableFields}
          tableUserFields={tableUserFields}
          open={isTableTooltipOpen}
          handleTableTooltip={setTableTooltipOpen}
        />
      </Box>

      <Box sx={listPatientFilters}>
        <Box sx={{ width: '100%', marginRight: '-18px' }}>
          <Combobox<{ title: string; value: string }>
            label="Поиск по"
            placeholder="Поиск по"
            options={searchTypeOptions}
            value={selectedSearchTypeOption?.value}
            searchable
            onChange={(value, option) => {
              if (value && option) {
                handleSearchType(value.value);
              }
            }}
          />
        </Box>
        <Box sx={{ width: '100%', marginLeft: '-20px' }}>
          <SpectrumTextField
            label=""
            placeholder="Введите значение"
            value={searchText}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              setSearchText(value);
              scheduleSearch(value);
            }}
            rightElement={<SearchIcon sx={{ color: '#BCBEC8', fontSize: '20px' }} />}
          />
        </Box>

        <Box sx={{ width: '100%' }}>
          <MultiCombobox<SearchParams>
            label="Программа"
            placeholder="Программа"
            options={programOptions}
            value={selectedProgramOptions.map((opt) => opt.value)}
            searchable
            clearable
            onChange={(values) => {
              sessionStorage.setItem('tableFilterPatientByPrograms', JSON.stringify(values || []));
              setSelectedStaffFilterPrograms(values || []);
            }}
          />
        </Box>

        <Box sx={{ width: '100%' }}>
          <MultiCombobox<SearchParams>
            label="Категория"
            placeholder="Категория"
            options={categoryOptions}
            value={selectedCategoryOptions.map((opt) => opt.value)}
            searchable
            clearable
            onChange={(values) => {
              sessionStorage.setItem('tableFilterPatientRelativeType', JSON.stringify(values || []));
              handleSelectedPatientType(values || []);
            }}
          />
        </Box>

        <Box sx={{ width: '100%' }}>
          <MultiCombobox<SearchParams>
            label="Тип пациентов"
            placeholder="Тип пациентов"
            options={patientTypeOptions}
            value={selectedPatientTypeOptions.map((opt) => opt.value)}
            searchable
            clearable
            onChange={(values) => {
              sessionStorage.setItem('tableFilterPatientByType', JSON.stringify(values || []));
              handleSelectedTypeStaff(values || []);
            }}
          />
        </Box>

        <Stack spacing={3} sx={{ width: '100% ' }}>
          <DatePicker
            mode="single"
            label="Период с"
            placeholder="Период с"
            value={startDateInput}
            onChange={(newValue) => {
              // Всегда обновляем вводимую строку, чтобы разрешить ручной ввод
              setStartDateInput(newValue || undefined);
              if (newValue) {
                try {
                  // Парсим дату из формата dd.MM.yyyy
                  const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                  if (isValid(parsedDate) && isDateActual(parsedDate)) {
                    handleStartDate(parsedDate);
                    setPatient({
                      ...currentPatient,
                      start_date: format(parsedDate, 'yyyy-MM-dd 00:00:00'),
                    });
                    sessionStorage.setItem(
                      'tableFilterPatientByStartDate',
                      JSON.stringify(format(parsedDate, 'yyyy-MM-dd 00:00:00'))
                    );
                  }
                } catch {
                  // Если не удалось распарсить, игнорируем
                }
              } else {
                handleStartDate(null);
                setPatient({
                  ...currentPatient,
                  start_date: null,
                });
                sessionStorage.setItem('tableFilterPatientByStartDate', JSON.stringify(''));
              }
            }}
            disabled={isMainSearchPending}
            size="s"
          />
        </Stack>

        <Stack spacing={3} sx={{ width: '100% ' }}>
          <DatePicker
            mode="single"
            label="Период до"
            placeholder="Период до"
            value={endDateInput}
            onChange={(newValue) => {
              // Всегда обновляем вводимую строку, чтобы разрешить ручной ввод
              setEndDateInput(newValue || undefined);
              if (newValue) {
                try {
                  // Парсим дату из формата dd.MM.yyyy
                  const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                  if (isValid(parsedDate) && isDateActual(parsedDate)) {
                    handleEndDate(parsedDate);
                    setPatient({
                      ...currentPatient,
                      end_date: format(parsedDate, 'yyyy-MM-dd 23:59:59'),
                    });
                    sessionStorage.setItem(
                      'tableFilterPatientByEndDate',
                      JSON.stringify(format(parsedDate, 'yyyy-MM-dd 23:59:59'))
                    );
                  }
                } catch {
                  // Если не удалось распарсить, игнорируем
                }
              } else {
                handleEndDate(null);
                setPatient({
                  ...currentPatient,
                  end_date: null,
                });
                sessionStorage.setItem('tableFilterPatientByEndDate', JSON.stringify(''));
              }
            }}
            disabled={isMainSearchPending}
            size="s"
          />
        </Stack>
      </Box>

      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#ffffff',
          padding: '20px',
          borderRadius: '12px',
          minHeight: isLoadingSearchPatients && !hasPatientRows ? 'min(360px, 70vh)' : undefined,
        }}
        aria-busy={isMainSearchPending}
      >
        <Box
          sx={{
            opacity: isMainSearchPending && hasPatientRows ? 0.45 : 1,
            pointerEvents: isMainSearchPending ? 'none' : 'auto',
          }}
        >
          <TableStaff
            handleCloseTableTooltip={() => setTableTooltipOpen(false)}
            handleCloseTypeSearchTooltip={() => {}}
            tableUserFields={tableUserFields}
            handleActionWithStaffTooltip={handleActionWithStaffTooltip}
            isAddingForbiddenByDates={isAddingForbiddenByDates}
            isDetachingForbiddenByDates={isDetachingForbiddenByDates}
            handleAvailabilityCompletedPatients={handleAvailabilityCompletedPatients}
            selectedCompanies={selectedCompanies}
            isPatients={isPatients}
            isFetchingNextPageSearchPatients={isFetchingNextPageSearchPatients}
            orderingTableStaff={orderingTableStaff}
            handleStaffCheck={handleStaffCheck}
            setSelectedStaff={setSelectedStaff}
            handleOrderingTableStaff={handleOrderingTableStaff}
            accessList={accessList}
            rolesData={rolesData}
            defaultPermissions={defaultPermissions}
            isSlotsAviable={isSlotsAviable}
            refetchSearchPatients={refetchSearchPatients}
          />
        </Box>
        {isMainSearchPending && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.42)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <Loader variant="default" />
          </Box>
        )}
      </Box>
    </Box>
  );
};
